import { motion, AnimatePresence } from 'framer-motion'
import { X, Users, Trash2, Link2, Clock, Phone, Mail } from 'lucide-react'
import { slideInRight } from '@/lib/motion'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { Modal } from '@/components/ui/Modal'
import { useFloorPlanStore } from '@/lib/store/useFloorPlanStore'
import { useUpdateTable, useSoftDeleteTable } from '@/lib/queries/useTables'
import type { Table, TableCombination, TableShape, SeatLayout } from '@/lib/types/table'
import type { Booking } from '@/lib/types/booking'
import { getDurationProgress } from '@/lib/utils/date'
import { STATUS_CONFIG } from '@/lib/utils/booking'
import { ShapePicker } from './ShapePicker'
import { useState, useEffect } from 'react'

interface TableDetailPanelProps {
  table: Table | null
  currentBooking: Booking | null
  combinations: TableCombination[]
  onClose: () => void
  onNewBooking: () => void
}

export function TableDetailPanel({ table, currentBooking, combinations, onClose, onNewBooking }: TableDetailPanelProps) {
  const { selectTable, mode } = useFloorPlanStore()
  const updateTable = useUpdateTable()
  const softDelete = useSoftDeleteTable()
  const [name, setName] = useState('')
  const [seatsMin, setSeatsMin] = useState(1)
  const [seatsMax, setSeatsMax] = useState(2)
  const [combinable, setCombinable] = useState(true)
  const [shape, setShape] = useState<TableShape>('square')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [seatLayout, setSeatLayout] = useState<SeatLayout>({ top: 0, bottom: 0, left: 0, right: 0 })

  const hasExplicitLayout = shape !== 'circle'

  useEffect(() => {
    if (table) {
      setName(table.name)
      setSeatsMin(table.seatsMin)
      setSeatsMax(table.seatsMax)
      setCombinable(table.combinable !== false)
      const s = table.shape ?? 'square'
      setShape(s)
      const stored = (table.metadata as any)?.seatLayout as SeatLayout | undefined
      if (stored) {
        setSeatLayout(stored)
      } else {
        // Derive a default layout from seatsMax
        setSeatLayout(deriveDefaultLayout(s, table.seatsMax))
      }
    }
  }, [table])

  function deriveDefaultLayout(s: TableShape, total: number): SeatLayout {
    if (s === 'circle') return { top: 0, bottom: 0, left: 0, right: 0 }
    if (s === 'rectangle') {
      const half = Math.floor(total / 2)
      return { top: half, bottom: total - half, left: 0, right: 0 }
    }
    // square: split evenly top/bottom
    const half = Math.floor(total / 2)
    return { top: half, bottom: total - half, left: 0, right: 0 }
  }

  function updateSeatSide(side: keyof SeatLayout, val: number) {
    const next = { ...seatLayout, [side]: Math.max(0, val) }
    setSeatLayout(next)
    setSeatsMax(next.top + next.bottom + next.left + next.right)
  }

  const handleClose = () => {
    selectTable(null)
    onClose()
  }

  const handleSave = () => {
    if (!table) return
    const existingMeta = (table.metadata as any) ?? {}
    const meta = hasExplicitLayout
      ? { ...existingMeta, seatLayout }
      : { ...existingMeta, seatLayout: undefined }
    updateTable.mutate({ documentId: table.documentId, name, seatsMin, seatsMax, combinable, shape, metadata: meta } as any)
  }

  const handleDelete = () => {
    if (!table) return
    softDelete.mutate({ id: table.id, documentId: table.documentId }, {
      onSuccess: () => {
        setShowDeleteConfirm(false)
        handleClose()
      },
    })
  }

  const tableCombinations = combinations.filter(
    (c) => c.tables?.some((t) => t.id === table?.id),
  )

  return (
    <>
      <AnimatePresence>
        {table && (
          <motion.div
            {...slideInRight}
            className="fixed right-0 top-0 bottom-0 w-80 bg-[var(--color-bg-surface)] border-l border-[var(--color-border-subtle)] z-50 flex flex-col shadow-2xl"
          >
            <div className="flex items-center justify-between p-4 border-b border-[var(--color-border-subtle)]">
              <div>
                <h3 className="font-display font-semibold text-[var(--color-text-primary)]">
                  {table.name}
                </h3>
                <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                  {table.seatsMax}p · {table.area?.name ?? 'No area'}
                </p>
              </div>
              <button
                onClick={handleClose}
                className="p-1 rounded-[var(--radius-sm)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)] transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Layout mode: full editing form */}
              {mode === 'layout' && (
                <>
                  <Input label="Table Name" value={name} onChange={(e) => setName(e.target.value)} />

                  <ShapePicker value={shape} onChange={(s) => {
                    setShape(s)
                    const layout = deriveDefaultLayout(s, seatsMax)
                    setSeatLayout(layout)
                  }} />

                  {/* Seat layout — per-side for rect/square, total for circle */}
                  <div>
                    <p className="text-sm font-medium text-[var(--color-text-secondary)] mb-2">Seat layout</p>
                    {hasExplicitLayout ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="w-16 text-xs text-[var(--color-text-muted)]">Top</span>
                          <input
                            type="number"
                            min={0}
                            value={seatLayout.top}
                            onChange={(e) => updateSeatSide('top', Number(e.target.value))}
                            className="flex-1 h-8 px-2 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-sm text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-accent-amber)]"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-16 text-xs text-[var(--color-text-muted)]">Bottom</span>
                          <input
                            type="number"
                            min={0}
                            value={seatLayout.bottom}
                            onChange={(e) => updateSeatSide('bottom', Number(e.target.value))}
                            className="flex-1 h-8 px-2 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-sm text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-accent-amber)]"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-16 text-xs text-[var(--color-text-muted)]">Left</span>
                          <input
                            type="number"
                            min={0}
                            value={seatLayout.left}
                            onChange={(e) => updateSeatSide('left', Number(e.target.value))}
                            className="flex-1 h-8 px-2 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-sm text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-accent-amber)]"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-16 text-xs text-[var(--color-text-muted)]">Right</span>
                          <input
                            type="number"
                            min={0}
                            value={seatLayout.right}
                            onChange={(e) => updateSeatSide('right', Number(e.target.value))}
                            className="flex-1 h-8 px-2 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-sm text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-accent-amber)]"
                          />
                        </div>
                        <p className="text-[10px] text-[var(--color-text-muted)]">Total: {seatLayout.top + seatLayout.bottom + seatLayout.left + seatLayout.right} seats</p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="w-16 text-xs text-[var(--color-text-muted)]">Total</span>
                        <input
                          type="number"
                          min={1}
                          value={seatsMax}
                          onChange={(e) => setSeatsMax(Number(e.target.value))}
                          className="flex-1 h-8 px-2 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-sm text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-accent-amber)]"
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-[var(--color-text-secondary)]">Area</span>
                    <Badge>{table.area?.name ?? 'None'}</Badge>
                  </div>

                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-[var(--color-text-secondary)]">Priority</span>
                    <span className="text-sm text-[var(--color-text-primary)]">{table.bookingPriority}</span>
                  </div>

                  <div className="flex items-center justify-between py-2">
                    <div>
                      <span className="text-sm text-[var(--color-text-secondary)]">Combinable</span>
                      <p className="text-xs text-[var(--color-text-muted)]">Allow combining with other tables</p>
                    </div>
                    <button
                      onClick={() => setCombinable((v) => !v)}
                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${combinable ? 'bg-[var(--color-accent-amber)]' : 'bg-[var(--color-bg-base)]'}`}
                    >
                      <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${combinable ? 'translate-x-4' : 'translate-x-0'}`} />
                    </button>
                  </div>

                  <Button variant="secondary" size="sm" onClick={handleSave} className="w-full" loading={updateTable.isPending}>
                    Save Changes
                  </Button>

                  {/* Combinations */}
                  {tableCombinations.length > 0 && (
                    <div className="border-t border-[var(--color-border-subtle)] pt-3">
                      <h4 className="text-sm font-medium text-[var(--color-text-secondary)] mb-2">Combinations</h4>
                      {tableCombinations.map((c) => (
                        <div key={c.id} className="flex items-center gap-2 p-2 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)]">
                          <Link2 className="h-3.5 w-3.5 text-[var(--color-accent-amber)] shrink-0" />
                          <span className="flex-1 text-sm">{c.name}</span>
                          <Badge>{c.seatsMax}p</Badge>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Delete */}
                  <div className="border-t border-[var(--color-border-subtle)] pt-3">
                    <Button variant="danger" size="sm" className="w-full" onClick={() => setShowDeleteConfirm(true)}>
                      <Trash2 className="h-3.5 w-3.5" /> Delete Table
                    </Button>
                  </div>
                </>
              )}

              {/* Guest / booking info — shown in both modes */}
              {currentBooking && (() => {
                const cfg = STATUS_CONFIG[currentBooking.status]
                const isActive = ['arrived', 'seated'].includes(currentBooking.status)
                return (
                  <div className={mode === 'layout' ? '' : 'pt-1'}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">Guest</span>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${cfg.color} ${cfg.textColor}`}>
                        {cfg.label}
                      </span>
                    </div>
                    <div className="p-3 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] space-y-2">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-[var(--color-accent-amber)] shrink-0" />
                        <span className="text-sm font-medium text-[var(--color-text-primary)] truncate">
                          {currentBooking.guest?.name || 'Unknown guest'}
                        </span>
                      </div>
                      {currentBooking.guest?.phone && (
                        <div className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
                          <Phone className="h-3 w-3 shrink-0" />
                          <span>{currentBooking.guest.phone}</span>
                        </div>
                      )}
                      {currentBooking.guest?.email && (
                        <div className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
                          <Mail className="h-3 w-3 shrink-0" />
                          <span className="truncate">{currentBooking.guest.email}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
                        <Clock className="h-3 w-3 shrink-0" />
                        <span>{currentBooking.time} &middot; {currentBooking.duration}min &middot; {currentBooking.people} guests</span>
                      </div>
                      {isActive && (
                        <div className="mt-1 h-1.5 rounded-full bg-[var(--color-bg-overlay)] overflow-hidden">
                          <div
                            className="h-full rounded-full bg-[var(--color-accent-amber)] transition-all duration-1000"
                            style={{ width: `${getDurationProgress(currentBooking.dateTime, currentBooking.duration)}%` }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )
              })()}

              {/* No booking message in live mode */}
              {mode === 'live' && !currentBooking && (
                <div className="text-center py-8 text-sm text-[var(--color-text-muted)]">
                  No booking at this time
                </div>
              )}


            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Modal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete Table?"
        size="sm"
      >
        <p className="text-sm text-[var(--color-text-secondary)] mb-4">
          Tables with upcoming bookings cannot be deleted. This table will be soft-deleted and hidden from the floor plan.
        </p>
        <div className="flex gap-2 justify-end">
          <Button variant="secondary" size="sm" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={handleDelete} loading={softDelete.isPending}>Delete</Button>
        </div>
      </Modal>
    </>
  )
}
